<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import type { MenuItemType } from '~/components/main/header/type';

const props = withDefaults(defineProps<{
  text: string
  type: MenuItemType
  variant?: 'default' | 'homepage' | 'sticky'
}>(), {
  variant: 'default',
});

const modelValue = defineModel<MenuItemType>();

const menuRef = ref<HTMLElement | null>(null);
const menuButtonRef = ref<HTMLElement | null>(null);
const isMounted = useMounted();

const toggleSubMenu = () => {
  modelValue.value = modelValue.value === props.type ? undefined : props.type;
};

onClickOutside(menuRef, () => {
  if (modelValue.value === props.type) {
    modelValue.value = undefined;
  }
}, {
  ignore: [menuButtonRef],
});
</script>

<template>
  <div class="relative font-semibold group-[.is-prestige]:font-normal">
    <div class="hidden lg:block">
      <button
        ref="menuButtonRef"
        class="inline-flex items-center gap-2xs group-[.is-prestige]:uppercase group-[.is-prestige]:tracking-wider"
        type="button"
        :aria-expanded="modelValue === type"
        :aria-controls="'submenu-' + type"
        :class="{ 'text-secondary group-[.is-prestige]:text-prestige-primary-40': modelValue === type }"
        @click="toggleSubMenu"
      >
        {{ text }}
        <Icon
          name="iad-icon:caret-down"
          class="size-4 transition-transform"
          :class="{ 'rotate-180': modelValue === type }"
        />
      </button>
    </div>
    <div
      class="lg:hidden flex w-full justify-between items-center cursor-pointer group-[.is-prestige]:uppercase group-[.is-prestige]:tracking-wider"
      @click="modelValue = type"
    >
      {{ text }}
      <IadBtn
        variant="outlined"
        size="small"
        icon
      >
        <Icon name="iad-icon:arrow-right" />
      </IadBtn>
    </div>
    <div
      ref="menuRef"
      class="flex flex-col bg-white p-m
        fixed lg:absolute inset-0 lg:inset-auto lg:left-0 lg:right-0 lg:top-2xl group-[.is-prestige]:lg:top-xl
        lg:min-w-max lg:max-w-[70vw] lg:rounded-b-xl z-10"
      :class="{
        hidden: modelValue !== type,
      }"
    >
      <div class="pb-2xs -mt-2xs lg:hidden">
        <IadBtn
          variant="outlined"
          icon
          @click="toggleSubMenu"
        >
          <Icon name="iad-icon:arrow-left" />
        </IadBtn>
      </div>
      <div
        v-if="variant === 'homepage' || isMounted"
        class="pt-l lg:pt-0 pb-m lg:pb-0 pr-s lg:pr-0 flex-1 overflow-auto"
      >
        <slot />
      </div>
    </div>
  </div>
</template>
